import { humanoidFace, humanoidSkull } from './humanoid-head.js';
import type { HumanFace, HumanRigId, HumanSkull } from './human.js';
import { NOSE_SHAPE_DEFAULT, type NoseShape } from './humanoid-nose.js';

/** Orc facial anatomy, separate from Human face variants. Distances are in reference skull space. */
export interface OrcFaceMorph extends NoseShape {
  jawWidth: number; underbite: number; cheekWidth: number; brow: number; noseWidth: number; noseProjection: number;
  noseLength: number; noseBridge: number; noseRootHeight: number;
  mouthWidth: number; lipFullness: number; eyeSize: number; eyeDepth: number;
  /** Forward offset of the eye-level cross-section from the matching Human skull. */
  eyeLevelCentreOffset: number;
  /** Orbital plane before the Eye depth inset; the morph control shows the resulting skull plane. */
  eyePlane: number; eyeOpening: number; earPoint: number;
  tuskLength: number; tuskRadius: number; tuskSpread: number; tuskForward: number; tuskCurl: number;
}
export const ORC_FACE_DEFAULT: OrcFaceMorph = {
  ...NOSE_SHAPE_DEFAULT,
  jawWidth: 1, underbite: .012, cheekWidth: 1, brow: .013, noseWidth: 1.4, noseProjection: .098,
  noseLength: .037, noseBridge: -.006, noseRootHeight: 1.858,
  mouthWidth: .033, lipFullness: 1.05, eyeSize: .98, eyeLevelCentreOffset: 0, eyePlane: .0835, eyeDepth: .007, eyeOpening: .78, earPoint: .048,
  tuskLength: .036, tuskRadius: .0068, tuskSpread: .82, tuskForward: .015, tuskCurl: .008,
};
/** Stable ids keep saved Orc faces valid; each now selects Orc anatomy rather than Human face deltas. */
export const orcFaces: { id: string; name: string; morph?: Partial<OrcFaceMorph> }[] = [
  { id: 'standard', name: 'Orc classic' },
  { id: 'broad', name: 'Heavy jaw', morph: { jawWidth: 1.14, mouthWidth: .037, underbite: .018, brow: .016, tuskRadius: .008, tuskLength: .042 } },
  { id: 'angular', name: 'High cheekbones', morph: { jawWidth: .96, cheekWidth: 1.1, brow: .011, eyeDepth: .010 } },
  { id: 'soft', name: 'Rounded', morph: { jawWidth: .95, underbite: .009, lipFullness: 1.2, brow: .009, tuskLength: .030 } },
  { id: 'aquiline', name: 'Long nose', morph: { noseProjection: .109, noseWidth: 1.22, underbite: .011 } },
  { id: 'gaunt', name: 'Lean', morph: { cheekWidth: .96, jawWidth: .94, eyeDepth: .011, lipFullness: .85 } },
  { id: 'youthful', name: 'Young', morph: { brow: .008, underbite: .008, eyeSize: 1.07, tuskLength: .027, tuskRadius: .0058 } },
];
export function resolveOrcFace(base: OrcFaceMorph, id = 'standard'): OrcFaceMorph {
  return { ...base, ...orcFaces.find(face => face.id === id)?.morph };
}

/** A broad mandible and oral plane, low heavy brow, wide nose and swept ears; on the shared male/female skull. */
export function orcSkull(m: OrcFaceMorph, body: HumanRigId): HumanSkull {
  const skull = humanoidSkull(body, 1.32 * m.jawWidth, 1.12 * m.cheekWidth, m.underbite);
  skull.jaw.centre[5] += m.eyeLevelCentreOffset;
  return {
    ...skull,
    eye: { x: .0345, y: 1.854, plane: m.eyePlane - m.eyeDepth * .5, opening: m.eyeOpening },
    ear: { ...skull.ear, length: .027, width: .017, thickness: .0075, pointed: true, pointLength: m.earPoint },
    mouth: { y: 1.798, width: m.mouthWidth, upper: .95, lower: 1.22, forward: 1.1, bow: .7, corners: -.0003, groove: .00075, overlap: .0007, lift: .0005 },
    nose: { rootY: m.noseRootHeight, length: m.noseLength, tipZ: m.noseProjection,
      ...Object.fromEntries(Object.keys(NOSE_SHAPE_DEFAULT).map(key => [key, m[key as keyof NoseShape]])) },
  };
}
export function orcFaceShape(m: OrcFaceMorph, body: HumanRigId = 'human-male'): HumanFace {
  return { ...humanoidFace(body, { jaw: 1, chin: 1.07, square: 1.4 }, { chinDepth: .003 }), brow: m.brow * .8, cheek: .034, hollow: .016, eye: m.eyeDepth, eyeSize: m.eyeSize,
    nose: m.noseWidth, lips: m.lipFullness, noseLength: -.06, bridge: m.noseBridge, fullness: 0, eyeTilt: -.015,
    fold: .0032 };
}
